import { useSyncExternalStore } from 'react'

export type Position = { x: number; y: number }
type PanelState = { connected: boolean; expanded: boolean; pos: Position | null }

export const STORAGE_KEY = 'voiso-softphone'
const OFF: PanelState = { connected: false, expanded: false, pos: null }

const readStored = (): PanelState => {
  try {
    const { connected = false, pos = null } = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '{}')

    return { connected, expanded: false, pos }
  } catch {
    return OFF
  }
}

// ponytail: module store shared by the nav button, the panel and click-to-call (same pattern as
// @lib/debugSettings); move to Redux if it grows.
const stored = readStored()
// Starts unmounted even if Voiso was connected before the reload; restoreIfMicAllowed decides below.
let panel: PanelState = { ...stored, connected: false }
const listeners = new Set<() => void>()

const setPanel = (partial: Partial<PanelState>) => {
  panel = { ...panel, ...partial }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ connected: panel.connected, pos: panel.pos }))
  } catch {
    /* storage blocked: the agent just reconnects after a reload */
  }
  listeners.forEach((listener) => listener())
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
  }
}

export const useVoisoPanel = () =>
  useSyncExternalStore(
    subscribe,
    () => panel,
    () => OFF,
  )

export const openVoisoSoftphone = () => setPanel({ connected: true, expanded: true })
export const minimizeVoisoSoftphone = () => setPanel({ expanded: false })
export const saveVoisoPanelPosition = (pos: Position) => setPanel({ pos })

// Voiso asks for the microphone as soon as its iframe loads. Re-mount it on page load only when the mic is
// already allowed (no prompt, and the phone keeps ringing); otherwise wait for the agent to open Voiso or
// click a number, so Chrome's prompt shows up at that moment instead of on every page load.
const restoreIfMicAllowed = async () => {
  try {
    const { state } = await navigator.permissions.query({ name: 'microphone' as PermissionName })
    if (state === 'granted') setPanel({ connected: true })
  } catch {
    /* no Permissions API: wait for the agent to open Voiso */
  }
}

if (stored.connected) void restoreIfMicAllowed()
